import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode, getTemplateNodeReturns, externalId, NotReady, WaitReady, TypeDescriptors, visitNone} from "@cm/template-nodes"
import type {
    OnCompileContext,
    TemplateNodeContext,
    TemplateNodeDefinition,
    VisitorNodeResult,
    ExternalId,
    IGetInfoNodeParts,
    BuilderInlet,
    BuilderOutlet,
    GraphBuilderScope,
    NodeEvaluator,
    TemplateNode,
} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import {MattressReferenceFrame} from "#mattress/nodes/reference-frame/mattress-reference-frame"
import type {MattressReferenceFrameData} from "#mattress/nodes/reference-frame/mattress-reference-frame"
import {
    mattressReferenceFrameToGlobalUVSpace,
    mattressReferenceFrameFromGlobalUVSpace,
    aliasReferenceMattressFrameData,
    resolveMattressReferenceFrameData,
    mattressReferenceFramesDataListsPush,
    getMattressCompileContext,
} from "#mattress/nodes/component/mattress-component-helpers"
import {MattressReferenceFrameInfo} from "#mattress/nodes/component/mattress-component-interface-descriptors"
import type {
    EvaluatedMattressComponentInput,
    EvaluatedMattressComponentInputs,
    EvaluatedMattressComponentValue,
    EvaluatedMattressComponentValueOrigin,
    EvaluatedMattressComponentValueType,
    MattressNodePath,
} from "#mattress/nodes/component/mattress-component-types"
import {mattressReferenceFrameLikeMarker} from "#mattress/role-markers"
import type {MattressReferenceFrameLikeNode} from "#mattress/role-markers"

import type {Vector2Like} from "@cm/math"

const TD = TypeDescriptors

const mattressNodePathArrayDesc = TD.Array(TD.Identity<MattressNodePath[number]>())

type ResolvedEvaluatedMattressComponentInput<V extends EvaluatedMattressComponentValue, O extends EvaluatedMattressComponentValueOrigin> = Omit<
    EvaluatedMattressComponentInput<EvaluatedMattressComponentValueType, O, V | null>,
    "type"
>

const resolveComponentInput = <
    T extends EvaluatedMattressComponentValueType,
    O extends EvaluatedMattressComponentValueOrigin,
    V extends EvaluatedMattressComponentValue,
>(
    entry: EvaluatedMattressComponentInput,
    type: T,
    externalId: string,
): ResolvedEvaluatedMattressComponentInput<V, O> => {
    if (entry.type === type) {
        return {value: entry.value as V, origin: entry.origin as O}
    } else throw Error(`Type mismatch for mattress component input ${externalId}: expected ${type}, got ${entry.type}`)
}

const getComponentInput = <
    T extends EvaluatedMattressComponentValueType,
    O extends EvaluatedMattressComponentValueOrigin,
    V extends EvaluatedMattressComponentValue,
>(
    node: TemplateNode,
    scope: GraphBuilderScope,
    componentInputs: EvaluatedMattressComponentInputs,
    externalId: string,
    type: T,
): BuilderInlet<ResolvedEvaluatedMattressComponentInput<V, O>> | undefined => {
    const entry = componentInputs[externalId]

    if (entry === undefined) return undefined

    if (entry !== NotReady) {
        return resolveComponentInput<T, O, V>(entry, type, externalId)
    }

    const waitReady = scope.node(WaitReady<EvaluatedMattressComponentInput>, {
        input: entry,
    })

    return scope.pureLambda(
        scope.tuple(waitReady.output as BuilderOutlet<EvaluatedMattressComponentInput>, type, externalId),
        ([entry, type, externalId]) => resolveComponentInput<T, O, V>(entry, type, externalId),
        `componentInput-${node.instanceId}`,
    )
}

export type MattressReferenceFrameInputParameters = {
    name: string
    id: ExternalId
    default?: MattressReferenceFrame
}

const definition: TemplateNodeDefinition<MattressReferenceFrameInputParameters> = {
    nodeClass: "MattressReferenceFrameInput",
    validation: {
        parameters: z.object({
            name: z.string(),
            id: externalId,
            default: z.optional(graphNode(MattressReferenceFrame)),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MattressReferenceFrameInput extends MattressNode<MattressReferenceFrameInputParameters> implements MattressReferenceFrameLikeNode {
    get [mattressReferenceFrameLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressReferenceFrameInputParameters> {
        const {id, name, default: defaultValue} = this.parameters
        const mattressContext = getMattressCompileContext(context)
        const {evaluator} = context
        const {templateScope} = evaluator
        const {componentInputs, evaluatedData, nodePath} = mattressContext
        const {componentsDescriptorLists, referenceFramesDataLists} = evaluatedData

        const scope = evaluator.getScope(this)

        const defaultInputData = scope.struct<ResolvedEvaluatedMattressComponentInput<MattressReferenceFrameData, MattressReferenceFrameLikeNode>>(
            "MattressReferenceFrameInputData",
            {
                value: defaultValue ? evaluator.evaluate(defaultValue) : null,
                origin: defaultValue,
            },
        )
        const inputData =
            getComponentInput<"mattressReferenceFrame", MattressReferenceFrameLikeNode, MattressReferenceFrameData>(
                this,
                templateScope,
                componentInputs,
                id,
                "mattressReferenceFrame",
            ) ?? defaultInputData

        componentsDescriptorLists.push(
            scope.pureLambda(
                scope.tuple(id, name, inputData),
                ([id, name, {value, origin}]) => [
                    new MattressReferenceFrameInfo({
                        id,
                        name,
                        value,
                        type: "input",
                        origin,
                    }),
                ],
                "MattressCurveInfo",
            ),
        )

        const newNodePath = scope.pureLambda(scope.tuple(nodePath, this), ([nodePath, instance]) => [...nodePath, instance], "newPath", {
            output: mattressNodePathArrayDesc,
        })
        const referenceFrameData = scope.pureLambda(
            scope.tuple(scope.get(inputData, "value"), newNodePath),
            ([outputData, newNodePath]) => {
                if (outputData === null) return null
                return {...outputData, nodePath: newNodePath}
            },
            "referenceFrameData",
        )

        mattressReferenceFramesDataListsPush(scope, referenceFramesDataLists, referenceFrameData)
        aliasReferenceMattressFrameData(this, evaluator, referenceFrameData)

        return visitNone(this.parameters)
    }

    toGlobalUVSpace(uv: Vector2Like, context: IGetInfoNodeParts): Vector2Like {
        return mattressReferenceFrameToGlobalUVSpace(this, uv, context)
    }

    fromGlobalUVSpace(uv: Vector2Like, context: IGetInfoNodeParts): Vector2Like {
        return mattressReferenceFrameFromGlobalUVSpace(this, uv, context)
    }

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderOutlet<MattressReferenceFrameData | null> {
        return resolveMattressReferenceFrameData(this, scope, evaluator)
    }
}
